// Îlots du marché (D-300) : découpage, précision, avancement du recensement et des attributions,
// ouverture sur le terrain pour le relevé et le recensement, renumérotation historisée d'un îlot.
import type { Numerotation } from '@cc/contrats';
import { Bandeau, Bouton, Champ, LienBouton, Selection } from '@cc/ui';
import { useState } from 'react';
import { useAction } from '../../commun/useAction';
import { useSession } from '../../session/FournisseurSession';
import { peut } from '../../session/permissions';
import { INVALIDER_MARCHES } from './api';
import { renumeroterIlot, useIlots, type Ilot } from './plan-api';
import styles from './marches.module.css';

function Renumerotation({ ilot }: { ilot: Ilot }) {
  const [regle, setRegle] = useState<Numerotation>(ilot.numerotation);
  const [motif, setMotif] = useState('');
  const action = useAction({
    executer: () =>
      renumeroterIlot(ilot.id, { numerotation: regle, motif, version_attendue: ilot.version }),
    succes: (r) =>
      `${r.renommees} places renumérotées ; les anciens numéros restent dans l’historique.`,
    invalider: INVALIDER_MARCHES,
  });
  return (
    <form
      className={styles.barreFiltres}
      aria-label={`Renuméroter l’îlot ${ilot.code}`}
      onSubmit={(e) => {
        e.preventDefault();
        action.lancer(undefined);
      }}
    >
      <Selection
        libelle="Règle"
        value={`${regle.format}:${regle.sens}`}
        onChange={(e) => {
          const [format, sens] = e.target.value.split(':') as [
            Numerotation['format'],
            Numerotation['sens'],
          ];
          setRegle({ ...regle, format, sens });
        }}
        options={[
          { valeur: 'ilot_rangee_rang:meme_sens', libelle: 'Îlot, rangée, rang' },
          { valeur: 'ilot_rangee_rang:alterne', libelle: 'Îlot, rangée, rang en serpentin' },
          { valeur: 'ilot_continu:meme_sens', libelle: 'Numéro continu' },
          { valeur: 'ilot_continu:alterne', libelle: 'Numéro continu en serpentin' },
        ]}
      />
      <Champ
        libelle="Motif de la renumérotation"
        value={motif}
        onChange={(e) => setMotif(e.target.value)}
        maxLength={300}
        required
      />
      <Bouton
        type="submit"
        variante="secondaire"
        disabled={motif.trim().length < 3 || action.enCours}
      >
        Renuméroter l’îlot
      </Bouton>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </form>
  );
}

export function ListeIlots({ marcheId, edition }: { marcheId: string; edition: boolean }) {
  const ilots = useIlots(marcheId);
  const { contexte } = useSession();
  const terrain =
    peut(contexte, 'marche_municipal', 'modifier') && peut(contexte, 'intervention', 'creer');
  const [ouvert, setOuvert] = useState<string | null>(null);
  if (ilots.error) return <Bandeau type="erreur">Les îlots n’ont pas pu être chargés.</Bandeau>;
  if (!ilots.data?.length)
    return (
      <p className={styles.aide}>
        Aucun îlot : dessinez-en un sur la carte ou importez un plan levé.
      </p>
    );
  return (
    <div className={styles.pile}>
      <table className={styles.tableauPlan}>
        <caption>Îlots du marché ({ilots.data.length})</caption>
        <thead>
          <tr>
            <th scope="col">Îlot</th>
            <th scope="col">Découpage</th>
            <th scope="col">Précision</th>
            <th scope="col">Recensées</th>
            <th scope="col">Attribuées</th>
            <th scope="col">Doublons</th>
            <th scope="col">Actions</th>
          </tr>
        </thead>
        <tbody>
          {ilots.data.map((i) => (
            <tr key={i.id}>
              <th scope="row">
                <span className="cc-mono">{i.code}</span> · {i.nom}
              </th>
              <td>
                {i.rangees} × {i.places_par_rangee} ({i.avancement?.places ?? 0} places)
              </td>
              <td>{i.precision === 'terrain' ? 'Relevé terrain' : 'Schématique'}</td>
              <td>
                {i.avancement?.recensees ?? 0} sur {i.avancement?.places ?? 0}
              </td>
              <td>{i.avancement?.attribuees ?? 0}</td>
              <td>{i.avancement?.doublons ?? 0}</td>
              <td>
                <div className={styles.actions}>
                  {terrain ? (
                    <LienBouton petit variante="secondaire" href={`/terrain/ilots/${i.id}`}>
                      Ouvrir sur le terrain
                    </LienBouton>
                  ) : null}
                  {edition ? (
                    <Bouton
                      petit
                      variante="discret"
                      aria-pressed={ouvert === i.id}
                      onClick={() => setOuvert(ouvert === i.id ? null : i.id)}
                    >
                      Renuméroter
                    </Bouton>
                  ) : null}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {ouvert && edition ? (
        <Renumerotation key={ouvert} ilot={ilots.data.find((i) => i.id === ouvert)!} />
      ) : null}
      <LienBouton variante="secondaire" href={`/marches/${marcheId}/recensement`}>
        Recensement et commerçants
      </LienBouton>
    </div>
  );
}
